import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import { toast } from 'sonner';
import api from '../services/api';
import { Mail, Lock, ArrowRight, Check, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import LanguageSelector from '../components/LanguageSelector';
import usePageTitle from '../hooks/usePageTitle';
import useCanonical from '../hooks/useCanonical';

// Default brand colors
const DEFAULT_BLUE = '#2B4B6A';
const DEFAULT_ORANGE = '#F58A5A';
const BRAND_BLUE = '#2B4B6A';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountType, setAccountType] = useState('');
  const [accountTypes, setAccountTypes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [platformTheme, setPlatformTheme] = useState(null);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const { company, isSubdomain, loading: subdomainLoading } = useSubdomain();
  
  // Get referral parameters from URL. A short invite link (/register/7f3k2q)
  // carries the same pair in the path, so resolve that first and fall back to
  // the long ?ref_user=&ref_company= form.
  const { code: inviteCode } = useParams();
  const [resolvedRef, setResolvedRef] = useState(null);

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    api.get(`/public/selling/code/${encodeURIComponent(inviteCode)}`)
      .then((r) => {
        if (cancelled) return;
        // Branding is resolved from the host, so an invite for a company with
        // its own subdomain has to be served from that subdomain to show the
        // company's logo and colours. Links generated now already point there;
        // this catches ones already shared as www links.
        const sub = r.data?.subdomain;
        if (sub && window.location.hostname === 'www.trados.app') {
          window.location.replace(
            `https://${sub}.trados.app/register/${encodeURIComponent(inviteCode)}${window.location.search}`
          );
          return;
        }
        setResolvedRef(r.data);
      })
      .catch(() => { if (!cancelled) setResolvedRef(null); });
    return () => { cancelled = true; };
  }, [inviteCode]);

  const refUser = resolvedRef?.ref_user || searchParams.get('ref_user');
  const refCompany = resolvedRef?.ref_company || searchParams.get('ref_company');
  const invitedEmail = searchParams.get('email');
  const selectedPlan = searchParams.get('plan');

  // Pre-populate email from URL if provided (from invitation link)
  useEffect(() => {
    if (invitedEmail) {
      setEmail(decodeURIComponent(invitedEmail));
    }
  }, [invitedEmail]);

  // Fetch platform theme for main domain
  useEffect(() => {
    if (!isSubdomain) {
      const fetchPlatformTheme = async () => {
        try {
          const res = await api.get('/settings/public-theme');
          setPlatformTheme(res.data);
        } catch (error) {
          console.log('Using default theme');
        }
      };
      fetchPlatformTheme();
    }
  }, [isSubdomain]);

  // Get company branding colors (for subdomain) or platform theme (for main domain)
  const primaryColor = isSubdomain 
    ? (company?.theme_settings?.primary_color || DEFAULT_BLUE)
    : (platformTheme?.primary_color || DEFAULT_BLUE);
  const accentColor = isSubdomain 
    ? (company?.theme_settings?.accent_color || DEFAULT_ORANGE)
    : (platformTheme?.secondary_color || DEFAULT_ORANGE);
  const buttonBg = isSubdomain 
    ? (company?.theme_settings?.primary_color || DEFAULT_BLUE)
    : (platformTheme?.button_primary_bg || DEFAULT_BLUE);
  const buttonText = isSubdomain 
    ? '#FFFFFF'
    : (platformTheme?.button_primary_text || '#FFFFFF');
  const pageBgColor = isSubdomain 
    ? (company?.theme_settings?.external_page_bg || '#F9FAFB')
    : (platformTheme?.page_bg || '#F9FAFB');
  
  const companyName = company?.name || 'Trados';

  // Set page title based on subdomain
  usePageTitle(isSubdomain ? `Register - ${companyName}` : 'Register');
  // Plain /register only — invite links (/register/:code) and subdomain tenants aren't the canonical page
  useCanonical(!isSubdomain && !inviteCode ? '/register' : null);

  useEffect(() => {
    // Fetch account types
    const fetchAccountTypes = async () => {
      try {
        const response = await api.get('/account-types');
        setAccountTypes(response.data);
      } catch (err) {
        console.error('Failed to fetch account types:', err);
        // Fallback to default types
        setAccountTypes([
          { name: 'Buyer', description: 'Purchase commodities from sellers' },
          { name: 'Seller', description: 'Sell commodities to buyers' },
          { name: 'Buyer/Seller', description: 'Both buy and sell commodities' },
          { name: 'Broker', description: 'Facilitate transactions between buyers and sellers' }
        ]);
      }
    };
    fetchAccountTypes();
    
    // Check if Google OAuth is enabled
    const checkGoogleOAuth = async () => {
      try {
        const res = await api.get('/integrations/google-oauth/status');
        setGoogleOAuthEnabled(res.data.enabled);
      } catch (error) {
        setGoogleOAuthEnabled(false);
      }
    };
    checkGoogleOAuth();
  }, []);

  // Handle Google OAuth login/register
  const handleGoogleSignUp = async () => {
    try {
      const currentOrigin = window.location.origin;
      const response = await api.get('/auth/google/url', {
        params: { 
          redirect_uri: window.location.origin + '/auth/google/callback',
          origin: currentOrigin
        }
      });
      
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error('Google Sign-In is not properly configured');
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      toast.error('Failed to initialize Google Sign-In');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!accountType) {
      setError('Please select an account type');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const accountTypeKey = accountType.toLowerCase().replace('/', '_').replace(' ', '_');
      const registerData = { 
        email, 
        password,
        account_type: accountTypeKey
      };
      
      // If on subdomain, include company_id
      if (isSubdomain && company?.id) {
        registerData.company_id = company.id;
      }
      
      // Include referral tracking if present (exclude literal "None" string)
      if (refUser && refUser !== 'None' && refUser !== 'null') {
        registerData.ref_user = refUser;
      }
      if (refCompany && refCompany !== 'None' && refCompany !== 'null') {
        registerData.ref_company = refCompany;
      }
      
      const response = await api.post('/auth/register', registerData);
      
      // Login the user with token from registration
      loginWithToken(response.data.access_token, response.data.user);
      toast.success('Account created successfully!');
      if (selectedPlan) {
        navigate('/admin/billing?plan=' + selectedPlan);
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while detecting subdomain
  if (subdomainLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="w-10 h-10 border-3 rounded-full animate-spin"
             style={{ borderColor: '#E5E7EB', borderTopColor: '#2B4B6A', borderWidth: '3px' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: pageBgColor }}>
      {/* Main domain: WP marketing chrome. Subdomain: company-branded header.
          PublicHeader owns that branch — do NOT render WPChrome directly here,
          or tenant subdomains get the Trados marketing header. */}
      <PublicHeader />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
          >
            {/* Language Selector - positioned above the header */}
            <div className="flex justify-end mb-4">
              <LanguageSelector variant="default" />
            </div>

            {/* Register a Company — prominent CTA at the TOP (main domain only).
                Tenant subdomains skip this since the user is already inside a
                company context. */}
            {!isSubdomain && (
              <Link
                to={`/register/company${window.location.search || ''}`}
                className="mb-6 rounded-xl border-2 p-5 flex items-center justify-between gap-4 hover:shadow-md transition-all"
                style={{ borderColor: '#F5A623', background: '#FFFDF5' }}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: '#F5A623', color: '#1E2A35' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 9h1M9 13h1M9 17h1M13 9h1M13 13h1M13 17h1"/></svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-extrabold" style={{ color: '#1E2A35' }}>
                      Register your Company →
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Set up your own branded workspace with a subdomain. Recommended for businesses.
                    </p>
                  </div>
                </div>
              </Link>
            )}

            {/* Divider between company CTA and individual signup form */}
            {!isSubdomain && (
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Or join as individual user</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            )}

            {/* Header — no in-card logo: the branded PublicHeader already shows
                the company logo, and light logos are invisible on the white card */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Create Account</h1>
              <p className="text-gray-500 text-sm">
                {isSubdomain
                  ? `Join ${companyName} and start trading`
                  : 'Join Trados and start trading globally'
                }
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    placeholder="you@company.com"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Account Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {accountTypes.map((type) => (
                    <button
                      key={type.name}
                      type="button"
                      onClick={() => setAccountType(type.name)}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        accountType === type.name
                          ? 'border-2 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      style={accountType === type.name ? { borderColor: BRAND_BLUE } : {}}
                      data-testid={`account-type-${type.name.toLowerCase().replace('/', '-')}`}
                    >
                      {accountType === type.name && (
                        <div 
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: BRAND_BLUE }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <p className="font-medium text-gray-900 text-sm">{type.name}</p>
                      {type.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{type.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                    placeholder="Min. 6 characters"
                    required
                    data-testid="register-password"
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                    placeholder="••••••••"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-medium transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{ backgroundColor: buttonBg, color: buttonText }}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Google OAuth Sign Up */}
            {googleOAuthEnabled && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-500">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  className="w-full flex items-center justify-center gap-3 py-3 px-6 border border-gray-200 rounded-xl font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all hover:shadow-md"
                  data-testid="google-signup-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
              </>
            )}

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="font-medium transition"
                  style={{ color: accentColor }}
                >
                  Sign in
                </Link>
              </p>
            </div>

            {/* Subdomain info */}
            {isSubdomain && (
              <div className="mt-4 text-center">
                <p className="text-gray-400 text-xs">
                  Powered by <a href="https://trados.app" className="hover:underline" style={{ color: primaryColor }}>Trados</a>
                </p>
              </div>
            )}
          </motion.div>

          {/* Info Note */}
          <p className="text-center text-gray-500 text-xs mt-6">
            {isSubdomain 
              ? `After registration, complete your verification to start using ${companyName}.`
              : 'After registration, complete your Bronze verification to start using Trados.'
            }
          </p>
        </div>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default RegisterPage;
